import type { CSSProperties, ReactNode } from 'react';
import { Save } from 'lucide-react';
import { Button } from '../ui/Button';
import { cn } from '../ui/utils';

export type FormStickyFooterProps = {
  saveLabel?: string;
  loading?: boolean;
  loadingText?: string;
  disabled?: boolean;
  saveButtonType?: 'submit' | 'button';
  onSave?: () => void;
  saveIcon?: ReactNode;
  saveVariant?: 'primary' | 'ghost' | 'danger';
  backHref?: string;
  onBack?: () => void;
  backLabel?: string;
  backIcon?: ReactNode;
  cancelHref?: string;
  onCancel?: () => void;
  cancelLabel?: string;
  hint?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
};

export function FormStickyFooter({
  saveLabel = 'Salvar alterações',
  loading = false,
  loadingText = 'Salvando...',
  disabled = false,
  saveButtonType = 'submit',
  onSave,
  saveIcon = <Save size={16} strokeWidth={2} />,
  saveVariant = 'primary',
  backHref,
  onBack,
  backLabel = 'Voltar',
  backIcon,
  cancelHref,
  onCancel,
  cancelLabel,
  hint,
  actions,
  children,
  className,
  style,
}: FormStickyFooterProps) {
  if (children) {
    return (
      <div className={cn('nx-form-page-footer nx-form-sticky-footer', className)} style={style}>
        {children}
      </div>
    );
  }

  const effectiveBackHref = cancelHref ?? backHref;
  const effectiveOnBack = onCancel ?? onBack;
  const effectiveBackLabel = cancelLabel ?? backLabel;

  return (
    <div className={cn('nx-form-page-footer nx-form-sticky-footer', className)} style={style}>
      <div className="nx-form-footer-left" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        {hint ? (
          <div className="nx-form-hint" style={{ margin: 0, fontSize: '0.8rem' }}>
            {hint}
          </div>
        ) : null}
      </div>

      <div className="nx-form-footer-right" style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', flexWrap: 'wrap' }}>
        {effectiveBackHref ? (
          <Button type="button" variant="ghost" disabled={loading} asChild>
            <a href={effectiveBackHref} style={{ textDecoration: 'none' }} className="inline-flex items-center gap-1.5">
              {backIcon}
              <span>{effectiveBackLabel}</span>
            </a>
          </Button>
        ) : effectiveOnBack ? (
          <Button
            type="button"
            variant="ghost"
            onClick={effectiveOnBack}
            disabled={loading}
            className="inline-flex items-center gap-1.5"
          >
            {backIcon}
            <span>{effectiveBackLabel}</span>
          </Button>
        ) : null}

        {actions}

        <Button
          type={saveButtonType}
          variant={saveVariant}
          loading={loading}
          loadingText={loadingText}
          disabled={disabled}
          onClick={onSave}
          className="inline-flex items-center gap-1.5"
        >
          {saveIcon}
          <span>{saveLabel}</span>
        </Button>
      </div>
    </div>
  );
}

export default FormStickyFooter;
